import React from 'react';
import PropTypes from 'prop-types';
import './suggested-person-annotation.css';

/**
 * Simplified annotation card for suggested person nodes.
 * Shows basic info (name, signal badges, confidence score).
 */
const SuggestedPersonAnnotation = ({ 
  position, 
  personName, 
  signals = [], 
  confidence,
  isCollapsed 
}) => {
  const { top, left } = getPosition(position, isCollapsed);
  
  const style = {
    top: `${top}px`,
    left: `${left}px`,
    position: 'absolute',
  };

  // Get initials for avatar placeholder
  const initials = personName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  const confidencePercent = confidence ? Math.round(confidence * 100) : null;

  return (
    <div className="suggested-person-annotation" style={style}>
      <div className="annotation-arrow" />
      <div className="suggested-person-card">
        <div className="card-header">
          <span className="suggested-badge">Suggested</span>
          <span className="node-type">Person</span>
        </div>
        <div className="card-content">
          <div className="avatar-section">
            <div className="avatar-placeholder" aria-hidden="true">
              {initials || '?'}
            </div>
          </div>
          <div className="info-section">
            <span className="person-name">{personName}</span>
            {confidencePercent && (
              <span className="confidence-score">{confidencePercent}% match</span>
            )}
          </div>
        </div>
        {signals.length > 0 && (
          <div className="signal-pills">
            {signals.map(signal => (
              <span key={signal} className={`pill ${signal}`}>
                {getSignalLabel(signal)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper to get human-readable signal labels
const getSignalLabel = (signal) => {
  const labels = {
    geographic: '📍 Geographic',
    coattendee: '🎫 Event',
    employer: '🏢 Employer',
  };
  return labels[signal] || signal;
};

const POPUP_BASE_WIDTH = 220;
const POPUP_BASE_HEIGHT = 180;
const POSITION_OFFSET = 50;
const VERTICAL_CENTER_OFFSET = 60;
const MIN_TOP_OFFSET = 100;

// Positioning logic similar to regular Annotation
const getPosition = (position, isCollapsed) => {
  const { x, y } = position;
  let left = x;
  let top = y;

  const remainingWidth = isCollapsed
    ? window.innerWidth - x
    : window.innerWidth - x - 300;
  const remainingHeight = window.innerHeight - y;
  
  // Horizontal positioning
  if (remainingWidth <= POPUP_BASE_WIDTH) {
    left = x - POPUP_BASE_WIDTH;
  } else {
    left = x + POSITION_OFFSET;
  }
  
  // Vertical positioning
  if (remainingHeight <= POPUP_BASE_HEIGHT) {
    top = y - POPUP_BASE_HEIGHT;
  } else if (y <= MIN_TOP_OFFSET) {
    top = y;
  } else {
    top = y - VERTICAL_CENTER_OFFSET;
  }
  
  return { top, left };
};

SuggestedPersonAnnotation.propTypes = {
  position: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }).isRequired,
  personName: PropTypes.string.isRequired,
  signals: PropTypes.arrayOf(PropTypes.string),
  confidence: PropTypes.number,
  isCollapsed: PropTypes.bool,
};

export default SuggestedPersonAnnotation;
